import React, { useState, useEffect, useMemo } from "react";
import { useInspectProgress } from "../../../../hooks/useInspectProgress";
import { useInspections } from "../../../../hooks/useInspections";
import { useHints } from "../../../../hooks/useHints";
import InspectProgressChip from "../../../../components/ui/InspectProgressChip";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";
import { getInspectKeys } from "../../../../data/inspectables";
import RaisedHandsBadge from "../../../../components/ui/RaisedHandsBadge";
import { ModalRegister } from "../../../../components/ui/ModalRegister";
import { useToast } from "../../../../components/ui/Toast";
import EmployeeEvaluator from "../../../origami/components/puzzles/EmployeeEvaluator";
import EvaluationDashboard from "../../../origami/components/puzzles/EvaluationDashboard";
import ExecutiveComputer from "../../../origami/components/ui/ExecutiveComputer";
import HintsModalHub from "../../../../components/ui/HintsModalHub";

const STAGE2_REAL = process.env.REACT_APP_STAGE2_REAL === "true";
const API = process.env.REACT_APP_API_URL;

export default function Six_Players_Stage2_Player6(props) {
  return STAGE2_REAL ? <RealStage2_Player6 {...props} /> : <PlaceholderStage2_Player6 {...props} />;
}

function PlaceholderStage2_Player6() {
  return (
    <div>
      <h2>🧪 Étape 2 — Joueur 6 - Version Bêta (placeholder)</h2>

      <p className="mt-2">
        Cette page est <strong>temporaire</strong> pour la bêta : l'énigme de l'étape 2 arrive bientôt.
        <br />
        Ici, on teste surtout que chaque joueur reçoit la <strong>bonne page</strong> et que la suite se synchronise.
      </p>

      <div className="mt-4 p-3 rounded bg-slate-900">
        <p className="font-semibold">✅ Ce que tu peux faire maintenant</p>
        <ul className="list-disc ml-5 mt-2">
          <li>Confirme à l'équipe que tu vois bien "Étape 2"</li>
          <li>Attends que le joueur responsable fasse avancer l'étape</li>
          <li>Vérifie que ton écran bascule bien vers l'étape 3</li>
        </ul>
      </div>

      <p className="mt-4 text-sm text-slate-500">
        Si tu restes bloqué(e) : rafraîchis la page (F5). Si ça persiste, préviens l'organisateur.
      </p>
    </div>
  );
}

function RealStage2_Player6({
  code,
  playerId,
  hintSignal,
  raisedCount,
  raisedPlayers,
  centerMaxH,
  pseudo,
}) {
  const toast = useToast();

  const INSPECT_KEYS = useMemo(() => {
    return getInspectKeys({
      gameId: 1,
      playerCount: 6,
      stage: 2,
      seat: 6,
    });
  }, []);

  const { markInspectedOnce } = useInspections({
    sessionCode: code,
    stage: 2,
    playerId: Number(playerId),
  });

  const { missionUnlock, missionResolve } = useMissionGuide({
    code,
    playerId,
    stage: 2,
  });

  const { markHintUsedOnce } = useHints({
    sessionCode: code,
    stage: 2,
    playerId: Number(playerId),
  });

  const [backgroundUrl] = useState("/assets/s2-p6-room.webp");
  const [bgReady, setBgReady] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);

  useEffect(() => {
    if (hintSignal > 0) {
      setShowHintModal(true);
    }
  }, [hintSignal]);

  const { inspectedCount, total } = useInspectProgress({
    sessionCode: code,
    stage: 2,
    playerId: Number(playerId),
    keys: INSPECT_KEYS,
  });

  // Evaluation states
  const [showDashboard, setShowDashboard] = useState(false);
  const [showEvaluator, setShowEvaluator] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [completedEmployees, setCompletedEmployees] = useState(() => {
    try {
      const stored = localStorage.getItem("s2p6_completed_evaluations");
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch (e) {
      return new Set();
    }
  });
  const [puzzleCompleted, setPuzzleCompleted] = useState(() => {
    try {
      return localStorage.getItem("s2p6_puzzle_completed") === "true";
    } catch (e) {
      return false;
    }
  });
  const [showDirective, setShowDirective] = useState(false);
  const [showPolicySimulator, setShowPolicySimulator] = useState(false);
  const [validatedPolicy, setValidatedPolicy] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [policyPuzzleSolved, setPolicyPuzzleSolved] = useState(() => {
    try {
      return localStorage.getItem("s2p6_policy_solved") === "true";
    } catch (e) {
      return false;
    }
  });

  const [showBooks, setShowBooks] = useState(false);
  const [showBottle, setShowBottle] = useState(false);
  const [showClock, setShowClock] = useState(false);
  const [showSculpture, setShowSculpture] = useState(false);
  const [showDrawers, setShowDrawers] = useState(false);

  const [sculptureFlipped, setSculptureFlipped] = useState(false);
  const [clockRunning, setClockRunning] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);

  const [selectedBook, setSelectedBook] = useState(null);

  useEffect(() => {
    setBgReady(false);
  }, [backgroundUrl]);

  // Handle employee evaluation requests from dashboard
  useEffect(() => {
    const handleEvaluateEmployee = (event) => {
      setCurrentEmployee(event.detail);
      setShowDashboard(false);
      setShowEvaluator(true);
    };

    window.addEventListener('evaluateEmployee', handleEvaluateEmployee);
    return () => window.removeEventListener('evaluateEmployee', handleEvaluateEmployee);
  }, []);

  // Persist completed employees
  useEffect(() => {
    try {
      localStorage.setItem("s2p6_completed_evaluations", JSON.stringify(Array.from(completedEmployees)));
    } catch (e) {
      console.error("Failed to save completed evaluations:", e);
    }
  }, [completedEmployees]);

  const handleEmployeeValidated = () => {
    if (currentEmployee) {
      const newCompleted = new Set(completedEmployees);
      newCompleted.add(currentEmployee.id);
      setCompletedEmployees(newCompleted);
      setShowEvaluator(false);
      setShowDashboard(true);
      toast?.success?.(`Évaluation de ${currentEmployee.name} validée !`);
    }
  };

  const handlePuzzleComplete = () => {
    setPuzzleCompleted(true);
    try {
      localStorage.setItem("s2p6_puzzle_completed", "true");
    } catch (e) {
      console.error("Failed to save puzzle state:", e);
    }

    missionUnlock({
      key: "s2.p6.executives.found",
      text: `${pseudo} a identifié les 3 comptes Executive de l'entreprise.`,
    });

    missionResolve({ key: "s2.p6.evaluation.found" })

    toast?.success?.("Liste des Executives identifiée !");
  };

  const handleSimulate = async (modules) => {
    setIsSimulating(true);

    try {
      const response = await fetch(`${API}/api/sessions/${code}/policy-simulation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          playerId: Number(playerId),
          modules // Envoie la liste des modules
        }),
      });

      if (!response.ok) throw new Error("Simulation failed");

      toast?.success?.("Simulation lancée ! Les résultats sont envoyés à la supervision.");
    } catch (error) {
      console.error("Error simulating policy:", error);
      toast?.error?.("Erreur lors de la simulation.");
    } finally {
      setTimeout(() => setIsSimulating(false), 1500);
    }
  };

  const handleValidate = async (simulationId) => {
    if (!simulationId) return null; // Retourner null si vide

    setValidatedPolicy(simulationId);

    try {
      const response = await fetch(`${API}/api/sessions/${code}/policy-validation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          playerId: Number(playerId),
          simulationId
        }),
      });

      const data = await response.json();

      if (data.correct) {
        setPolicyPuzzleSolved(true);
        localStorage.setItem("s2p6_policy_solved", "true");
        missionResolve({ key: "s2.p3.simulation.found" });


        toast?.success?.("✓ Politique validée ! Indice débloqué pour l'équipe.");
      } else {
        toast?.error?.("Cette politique n'est pas optimale. Vérifiez avec la supervision.");
        setValidatedPolicy(null);
      }

      return data; // Retourner le résultat
    } catch (error) {
      console.error("Error validating policy:", error);
      toast?.error?.("Erreur lors de la validation.");
      return null; // Retourner null en cas d'erreur
    }
  };

  // Fonction pour jouer le son "bip"
  const playBipSound = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();

    // Créer 3 bips courts
    [0, 200, 400].forEach(delay => {
      setTimeout(() => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800; // Fréquence du bip
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      }, delay);
    });
  };

  // useEffect pour gérer le timer
  useEffect(() => {
    if (!clockRunning) return;

    const timer = setTimeout(() => {
      setClockRunning(false);
      playBipSound();
      toast?.warning?.("⏰ Temps écoulé ! Retour au travail immédiatement.");
    }, 20000); // 20 secondes

    return () => clearTimeout(timer);
  }, [clockRunning, toast]);

  const TOXIC_BOOKS = [
    {
      id: 1,
      title: "Mange ou sois mangé",
      subtitle: "Guide du manager requin",
      author: "Gordon Proffit",
      color: "from-red-900 to-red-700",
      quote: "L'empathie est un luxe que les gagnants ne peuvent pas se permettre. Dans l'océan du business, il n'y a que deux catégories : les prédateurs et les proies. Choisissez votre camp. Les employés qui demandent de la 'reconnaissance' ou du 'feedback constructif' ont déjà perdu. Un vrai manager ne nourrit pas les méduses, il les élimine avant qu'elles ne contaminent le reste du banc.",
      hint: "Note manuscrite dans la marge : 'Les metrics de productivité ignorent systématiquement le facteur humain. C'est voulu. Les modules de surveillance appliquent ces principes à la lettre. Toute déviation = faiblesse.'",
      spine: "#8B0000"
    },
    {
      id: 2,
      title: "Les 7 habitudes des PDG",
      subtitle: "qui réussissent vraiment",
      author: "Richard Stoneheart",
      color: "from-slate-800 to-slate-600",
      quote: "Règle #3 : Un employé qui sourit est un employé qui n'est pas assez challengé. Le confort est l'ennemi de l'excellence. Si votre équipe rentre chez elle avant 20h, vous avez échoué en tant que leader. Règle #5 : Les congés sont une récompense, pas un droit. Accordez-les avec parcimonie, retirez-les sans préavis. Règle #7 : Ne célébrez jamais les victoires. Passez immédiatement à l'objectif suivant. L'auto-satisfaction tue plus d'entreprises que la concurrence.",
      spine: "#475569"
    },
    {
      id: 3,
      title: "Productivité 300%",
      subtitle: "La méthode GRIND",
      author: "Marcus Burnout",
      color: "from-orange-900 to-orange-700",
      quote: "Le sommeil est pour les faibles. La caféine est pour les champions. Chapitre 4 : 'Les 18 heures productives' - Voici comment restructurer votre journée : 4h de sommeil maximum, 18h de travail effectif, 2h de 'temps mort' (transport, repas express, douche rapide). Les weekends ? Un concept inventé par les syndicalistes pour saboter la croissance. Annexe B présente 47 cas d'étude d'executives qui n'ont pas pris de vacances depuis 6 ans. Tous sont millionnaires.",
      hint: "Post-it collé sur la page 47 : 'IMPORTANT - Les employés Executive doivent incarner ces valeurs 24/7. Surveillance comportementale active sur toutes leurs interactions avec ORIGAMI. Le moindre signe de faiblesse = alerte RH.'",
      spine: "#C2410C"
    },
    {
      id: 4,
      title: "Leadership Alpha",
      subtitle: "Dominer sans négocier",
      author: "Victor Kane",
      color: "from-purple-900 to-purple-700",
      quote: "Un bon manager ne demande pas d'avis. Il impose sa vision. Chapitre 2 : 'Le mythe de l'intelligence collective'. Vos employés ne sont pas payés pour penser. Ils sont payés pour exécuter. Chaque minute passée en 'brainstorming' est une minute de productivité perdue. Les réunions d'équipe ne devraient jamais dépasser 5 minutes : vous parlez, ils écoutent, ils exécutent. Point final.",
      spine: "#581C87"
    },
    {
      id: 5,
      title: "OKR ou KO",
      subtitle: "Objectifs non négociables",
      author: "Sarah Metrics",
      color: "from-emerald-900 to-emerald-700",
      quote: "Si vous n'atteignez pas 120% de vos objectifs, vous visez trop bas. Chapitre 7 : 'La culture du dépassement permanent'. Vos KPIs doivent être physiquement impossibles à atteindre. C'est le but. Quand un employé atteint 100% de ses objectifs, félicitez-le publiquement puis doublez ses objectifs pour le trimestre suivant. La complaisance est toxique. La pression est nutritive. Les employés qui craquent n'étaient pas faits pour l'excellence.",
      hint: "Surlignage fluo page 156 : 'Les systèmes d'évaluation automatisés par IA sont programmés pour pénaliser systématiquement les comportements 'humains' : pauses prolongées, conversations non professionnelles, recherches personnelles. C'est la norme Executive.'",
      spine: "#065F46"
    },
  ];

  const EMPLOYEE_FILES = {
    'M': [
      {
        id: 'marie',
        name: 'Marie Dupont',
        note: "REFUSE de travailler le weekend 'à cause de son chat'. INACCEPTABLE. Dose de Focus Max PRO+++ augmentée à 16 gélules/jour. Si résistance persiste → plan B.",
        flag: '🟡'
      },
    ],
    'S': [
      {
        id: 'samir',
        name: 'Samir Haddad',
        note: "TROP GENTIL. Organise des 'pauses café solidaires' ??? C'EST UNE ENTREPRISE PAS UNE GARDERIE. Surveiller de près. Potentiel saboteur de productivité.",
        flag: '🟡'
      },
      {
        id: 'martin',
        name: 'Martin Schmitt',
        note: "A osé dire 'préoccupations éthiques' en réunion Executive. GRAVISSIME. Le module de surveillance fait EXACTEMENT ce qu'il doit faire. Rééducation nécessaire.",
        flag: '🟠'
      },
    ],
    'T': [
      {
        id: 'thomas',
        name: 'Thomas Dubois',
        note: "A CLAQUÉ UNE PORTE pendant une réunion. INADMISSIBLE. Les portes coûtent cher. Les employés émotionnellement instables aussi. Sous surveillance maximale.",
        flag: '🔴'
      },
    ],
    'J': [
      {
        id: 'julien',
        name: 'Julien Perez',
        note: "Refuse de déployer après 17h. QUOI ??? Le code ne dort jamais. LES VRAIS DÉVELOPPEURS NON PLUS. Envisager remplacement par stagiaire moins fatigué.",
        flag: '🟡'
      },
    ],
    'A': [
      {
        id: 'alex',
        name: 'Alex Chen',
        note: "Mot de passe oublié 4x en 2 mois. INCOMPÉTENCE FLAGRANTE ou SABOTAGE DÉGUISÉ ??? Installer keylogger sur son poste. Pour sa sécurité, évidemment.",
        flag: '🟡'
      },
    ],
    'P': [
      {
        id: 'pdg',
        name: '[CONFIDENTIEL]',
        note: "🔒 Dossier verrouillé - Accès Executive uniquement",
        locked: true,
        flag: '⛔'
      },
    ],
  };

  const LETTERS = ['A', 'J', 'M', 'P', 'S', 'T'];

  const hintCategories = useMemo(() => [
    {
      id: "evaluations",
      title: "Évaluations annuelles",
      subtitle: "Compléter les évaluations en attente",
      hintKeyPrefix: "s2:p6:eval",
      levels: {
        l1: (
          <>
            <p className="mb-2">
              L'ordinateur contient un système d'évaluation RH. Certains employés ont déjà été évalués,
              d'autres attendent encore. Ton rôle est de compléter les évaluations manquantes.
            </p>
            <p className="text-xs text-gray-300">
              Lis attentivement le dossier de chaque employé avant de régler les curseurs — les points forts,
              points faibles, incidents et absences te donnent tous les éléments nécessaires.
            </p>
          </>
        ),
        l2: (
          <>
            <p className="mb-2">
              La grille de conversion est affichée dans chaque dossier : "Excellent" = 80-100, "Bon" = 60-80,
              "Correct" = 40-60, "Difficile" = 20-40. Traduis les qualificatifs du dossier en valeurs numériques.
            </p>
            <p className="text-xs text-gray-300">
              Les interdépendances entre critères s'appliquent automatiquement, si tu bouges un critère,
              un autre peut être ajusté en conséquence. C'est normal.
            </p>
          </>
        ),
        l3: (
          <>
            <p className="mb-2">
              Le bouton "Valider l'évaluation" ne s'active que quand tes valeurs sont dans les bonnes fourchettes.
              Si le bouton reste grisé, un ou plusieurs critères sont encore trop éloignés de la valeur attendue.
            </p>
            <p className="text-xs text-gray-300">
              Ajuste les critères un par un en te basant strictement sur le dossier.
            </p>
          </>
        ),
        answer: (
          <>
            <p className="text-sm text-red-200 font-semibold">Réponse (Valeurs cibles — tolérance ±10)</p>

            <div className="mt-3 space-y-4 text-xs">
              <div>
                <p className="font-bold text-amber-200 mb-1">Julien Perez (ID #147)</p>
                <div className="font-mono space-y-0.5 text-gray-200">
                  <p>Performance    → <span className="text-emerald-300">85</span></p>
                  <p>Collaboration  → <span className="text-emerald-300">30</span> <span className="text-slate-500">(auto-ajusté)</span></p>
                  <p>Initiative     → <span className="text-emerald-300">55</span></p>
                  <p>Adaptabilité   → <span className="text-emerald-300">75</span></p>
                  <p>Alignement     → <span className="text-emerald-300">30</span> <span className="text-slate-500">(auto-ajusté)</span></p>
                  <p>Disponibilité  → <span className="text-emerald-300">70</span></p>
                </div>
              </div>

              <div>
                <p className="font-bold text-amber-200 mb-1">Zoé Leroy (ID #181)</p>
                <div className="font-mono space-y-0.5 text-gray-200">
                  <p>Performance    → <span className="text-emerald-300">60</span></p>
                  <p>Collaboration  → <span className="text-emerald-300">90</span> <span className="text-slate-500">(auto-ajusté)</span></p>
                  <p>Initiative     → <span className="text-emerald-300">45</span></p>
                  <p>Adaptabilité   → <span className="text-emerald-300">50</span></p>
                  <p>Alignement     → <span className="text-emerald-300">85</span> <span className="text-slate-500">(auto-ajusté)</span></p>
                  <p>Disponibilité  → <span className="text-emerald-300">95</span></p>
                </div>
              </div>

              <div>
                <p className="font-bold text-amber-200 mb-1">Martin Schmitt (ID #194)</p>
                <div className="font-mono space-y-0.5 text-gray-200">
                  <p>Performance    → <span className="text-emerald-300">75</span></p>
                  <p>Collaboration  → <span className="text-emerald-300">55</span> <span className="text-slate-500">(auto-ajusté)</span></p>
                  <p>Initiative     → <span className="text-emerald-300">85</span></p>
                  <p>Adaptabilité   → <span className="text-emerald-300">90</span></p>
                  <p>Alignement     → <span className="text-emerald-300">35</span> <span className="text-slate-500">(auto-ajusté)</span></p>
                  <p>Disponibilité  → <span className="text-emerald-300">70</span></p>
                </div>
              </div>

              <p className="text-gray-400 text-[11px] italic">
                Les valeurs "auto-ajustées" sont re-calculées par le système et peuvent bouger quand tu règles les autres.
              </p>
            </div>
          </>
        ),
      },
    },
    {
      id: "policy_simulator",
      title: "Policy Simulator",
      subtitle: "Trouver la combinaison optimale",
      hintKeyPrefix: "s2:p6:policy",
      levels: {
        l1: (
          <>
            <p className="mb-2">
              Le simulateur te permet de combiner des modules de politique RH et d'envoyer les projections
              à la supervision. Elle analysera les résultats et t'indiquera si la combinaison
              est optimale.
            </p>
            <p className="text-xs text-gray-300">
              Commence par lire le mail dans l'application Mail, il explique ce qu'on attend de toi
              et débloque l'accès au simulateur.
            </p>
          </>
        ),
        l2: (
          <>
            <p className="mb-2">
              Il y a 6 modules disponibles. L'objectif est de maximiser la productivité tout en gardant
              stress et turnover sous contrôle. Certaines combinaisons sont clairement contre-productives
              (surveillance 24/7, heures sup obligatoires…).
            </p>
            <p className="text-xs text-gray-300">
              Chaque module a un impact sur 3 métriques : stress, turnover, productivité.
              Cherche les modules qui donnent beaucoup de productivité sans trop pénaliser le reste.
              La combinaison gagnante contient 3 modules.
            </p>
          </>
        ),
        l3: (
          <>
            <p className="mb-2">
              Les modules qui augmentent fortement la productivité sans exploser le stress sont dans les catégories
              "Performance". Parmi eux, le troisième module de la combinaison gagnante compense légèrement
              le stress généré par les deux premiers.
            </p>
            <p className="text-xs text-gray-300">
              Évite tout ce qui concerne la surveillance ou les pénalités, leur impact sur le stress
              dépasse largement leur bénéfice en productivité.
            </p>
          </>
        ),
        answer: (
          <>
            <p className="text-sm text-red-200 font-semibold">Réponse (Combinaison optimale)</p>
            <p className="mt-2 text-xs text-gray-200">
              La combinaison à envoyer en simulation :
            </p>
            <div className="mt-1 space-y-1 text-xs font-mono text-emerald-300">
              <p>✓ Augmentation quotas</p>
              <p>✓ Bonus performance</p>
              <p>✓ Réduction congés</p>
            </div>
            <p className="mt-2 text-xs text-gray-300">
              Sélectionne ces 3 modules, envoie la simulation au joueur 3, puis valide.
              La supervision confirmera que c'est la bonne combinaison.
            </p>
          </>
        ),
      },
    }
  ], []);

  return (
    <div className="relative min-h-screen w-full bg-black text-white flex items-center justify-center">
      {/* Cadre de jeu */}
      <div
        className="relative h-screen w-auto mx-auto aspect-square"
        style={{ maxHeight: centerMaxH ? `${centerMaxH}px` : undefined }}
      >
        <div className="absolute right-4 top-4 z-40 flex items-center gap-2 w-fit">
          <InspectProgressChip inspectedCount={inspectedCount} total={total} />
          <RaisedHandsBadge count={raisedCount} players={raisedPlayers} />
        </div>

        <div className="absolute left-4 top-4 rounded-full bg-black/70 border border-white/15 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-200 z-10">
          Bureau Exécutif
        </div>

        {/* IMAGE DE FOND */}
        <img
          src={backgroundUrl}
          alt="Bureau Exécutif"
          className="absolute inset-0 h-full w-full object-contain"
          onLoad={() => setBgReady(true)}
          onError={() => setBgReady(true)}
        />

        <div className={[
          "absolute inset-0 transition-opacity duration-200",
          bgReady ? "opacity-100" : "opacity-0 pointer-events-none",
        ].join(" ")}>
          {/* HOTSPOT: Computer - Evaluation System */}
          <img
            src="/assets/s2-p6-computer.webp"
            alt="Système d'évaluation"
            onClick={() => {
              markInspectedOnce('stage2player6:computer');
              missionUnlock({
                key: "s2.p6.evaluation.found",
                text: `${pseudo} a trouvé les évaluations annuelles des employés.`,
              });
              setShowDashboard(true)
            }
            }
            className="s2p6-hotspot-computer absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />

          {/* NOUVEAU HOTSPOT: Policy Simulator */}
          <img
            src="/assets/s2-p6-simulator.webp"
            alt="Simulateur de politique"
            onClick={() => {
              markInspectedOnce('stage2player6:simulator');
              setShowPolicySimulator(true)
            }
            }
            className="s2p6-hotspot-simulator absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />

          {/* HOTSPOT: Pile de livres */}
          <img
            src="/assets/s2-p6-books.webp"
            alt="Pile de livres"
            onClick={() => {
              markInspectedOnce('stage2player6:books');
              setShowBooks(true)
            }
            }
            className="s2p6-hotspot-books absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />

          {/* HOTSPOT: Flacon */}
          <img
            src="/assets/s2-p6-bottle.webp"
            alt="Flacon Focus Max"
            onClick={() => {
              markInspectedOnce('stage2player6:bottle');
              setShowBottle(true)
            }
            }
            className="s2p6-hotspot-bottle absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />

          {/* HOTSPOT: Horloge */}
          <img
            src="/assets/s2-p6-clock.webp"
            alt="Timer Pomodoro"
            onClick={() => {
              markInspectedOnce('stage2player6:clock');
              setShowClock(true)
            }
            }
            className="s2p6-hotspot-clock absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />

          {/* HOTSPOT: Sculpture */}
          <img
            src="/assets/s2-p6-sculpture.webp"
            alt="Sculpture Momentum"
            onClick={() => {
              markInspectedOnce('stage2player6:sculpture');
              setShowSculpture(true)
            }
            }
            className="s2p6-hotspot-sculpture absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />

          {/* HOTSPOT: Tiroirs */}
          <img
            src="/assets/s2-p6-drawers.webp"
            alt="Classeur confidentiel"
            onClick={() => {
              markInspectedOnce('stage2player6:drawers');
              setShowDrawers(true)
            }
            }
            className="s2p6-hotspot-drawers absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]"
          />
        </div>

        {/* MODALE: Dashboard */}
        <ModalRegister
          open={showDashboard}
          title="Système d'évaluation ORIGAMI"
          onClose={() => setShowDashboard(false)}
          containerClassName="!max-w-[1400px]"
          onHint={() => setShowHintModal(true)}
        >
          <EvaluationDashboard
            completedEmployees={completedEmployees}
            onComplete={handlePuzzleComplete}
            onClose={() => setShowDashboard(false)}
          />
        </ModalRegister>

        {/* MODALE: Employee Evaluator */}
        <ModalRegister
          open={showEvaluator}
          title={`Évaluation — ${currentEmployee?.name || ''}`}
          onClose={() => {
            setShowEvaluator(false);
            setShowDashboard(true);
          }}
          containerClassName="!max-w-[1600px]"
          onHint={() => setShowHintModal(true)}
        >
          {currentEmployee && (
            <EmployeeEvaluator
              employee={currentEmployee}
              onValidate={handleEmployeeValidated}
              onClose={() => {
                setShowEvaluator(false);
                setShowDashboard(true);
              }}
            />
          )}
        </ModalRegister>

        {/* MODALE: Policy Simulator */}
        <ModalRegister
          open={showPolicySimulator}
          title="" // Pas de titre car l'OS a son propre header
          onClose={() => setShowPolicySimulator(false)}
          containerClassName="!max-w-[1400px]"
          onHint={() => setShowHintModal(true)}
        >
          <ExecutiveComputer
            onSimulate={handleSimulate}
            onValidate={handleValidate}
            isSimulating={isSimulating}
            code={code}
            toast={toast}
          />
        </ModalRegister>

        {/* MODALE: Pile de livres */}
        <ModalRegister
          open={showBooks}
          title={selectedBook ? selectedBook.title : "Bibliothèque Executive"}
          onClose={() => {
            setShowBooks(false);
            setSelectedBook(null);
          }}
          containerClassName="!max-w-[900px]"
          onHint={() => setShowHintModal(true)}
        >
          {!selectedBook ? (
            /* Vue des piles */
            <div className="py-4">
              <div className="text-xs text-slate-400 mb-4 text-center">
                Une collection de livres de management soigneusement sélectionnés
              </div>

              {/* Deux piles côte à côte */}
              <div className="flex gap-8 justify-center items-end min-h-[420px]">
                {/* Pile 1 (livres 0-2) */}
                <div className="relative h-[400px] w-48">
                  {TOXIC_BOOKS.slice(0, 3).map((book, index) => (
                    <div
                      key={book.id}
                      className="absolute cursor-pointer transition-all hover:scale-105 hover:z-50 hover:brightness-110"
                      style={{
                        bottom: `${index * 70}px`,
                        left: `${index * 8}px`,
                        transform: `rotate(${(index % 2 === 0 ? 1 : -1) * (index * 3)}deg)`,
                        zIndex: index,
                      }}
                      onClick={() => setSelectedBook(book)}
                    >
                      <div className={`relative w-48 h-64 bg-gradient-to-br ${book.color} rounded-r-lg shadow-2xl border-r-4 border-black/30`}>
                        <div
                          className="absolute left-0 top-0 bottom-0 w-3 rounded-l-sm"
                          style={{ backgroundColor: book.spine }}
                        />
                        <div className="p-4 h-full flex flex-col justify-between">
                          <div>
                            <div className="text-xl font-bold text-white leading-tight mb-2">
                              {book.title}
                            </div>
                            <div className="text-sm text-white/80 italic">
                              {book.subtitle}
                            </div>
                          </div>
                          <div className="text-xs text-white/70">
                            {book.author}
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none rounded-r-lg" />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pile 2 (livres 3-4) */}
                <div className="relative h-[400px] w-48">
                  {TOXIC_BOOKS.slice(3, 5).map((book, index) => (
                    <div
                      key={book.id}
                      className="absolute cursor-pointer transition-all hover:scale-105 hover:z-50 hover:brightness-110"
                      style={{
                        bottom: `${index * 70}px`,
                        left: `${index * 8}px`,
                        transform: `rotate(${(index % 2 === 0 ? -1 : 1) * (index * 3)}deg)`,
                        zIndex: index,
                      }}
                      onClick={() => setSelectedBook(book)}
                    >
                      <div className={`relative w-48 h-64 bg-gradient-to-br ${book.color} rounded-r-lg shadow-2xl border-r-4 border-black/30`}>
                        <div
                          className="absolute left-0 top-0 bottom-0 w-3 rounded-l-sm"
                          style={{ backgroundColor: book.spine }}
                        />
                        <div className="p-4 h-full flex flex-col justify-between">
                          <div>
                            <div className="text-xl font-bold text-white leading-tight mb-2">
                              {book.title}
                            </div>
                            <div className="text-sm text-white/80 italic">
                              {book.subtitle}
                            </div>
                          </div>
                          <div className="text-xs text-white/70">
                            {book.author}
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none rounded-r-lg" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-xs text-slate-500 text-center italic mt-6">
                Cliquez sur un livre pour le lire
              </div>
            </div>
          ) : (
            /* Vue d'un livre ouvert */
            <div className="py-4">
              <button
                onClick={() => setSelectedBook(null)}
                className="text-xs text-cyan-400 hover:underline flex items-center gap-2 mb-4"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Retour aux piles
              </button>

              {/* Page du livre */}
              <div className={`rounded-lg bg-gradient-to-br ${selectedBook.color} p-8 shadow-2xl border-2 border-black/20 min-h-[400px]`}>
                <div className="bg-amber-50 p-6 rounded shadow-inner">
                  <div className="text-center mb-6">
                    <div className="text-2xl font-bold text-slate-900 mb-2">
                      {selectedBook.title}
                    </div>
                    <div className="text-sm italic text-slate-700 mb-1">
                      {selectedBook.subtitle}
                    </div>
                    <div className="text-xs text-slate-600">
                      par {selectedBook.author}
                    </div>
                  </div>

                  <div className="border-t-2 border-slate-300 pt-4 mb-6">
                    <div className="text-sm text-slate-800 italic leading-relaxed">
                      "{selectedBook.quote}"
                    </div>
                  </div>

                  {selectedBook.hint && (
                    <div className="mt-6 rounded border-2 border-amber-600 bg-amber-100 p-4">
                      <div className="text-xs text-amber-900 leading-relaxed">
                        {selectedBook.hint.replace('💡 Indice caché : ', '')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </ModalRegister>

        {/* MODALE: Flacon Focus Max */}
        <ModalRegister
          open={showBottle}
          title="Complément Executive"
          onClose={() => setShowBottle(false)}
          containerClassName="!max-w-[700px]"
          onHint={() => setShowHintModal(true)}
        >
          <div className="py-4">
            {/* Flacon visuel */}
            <div className="flex justify-center mb-6">
              <div className="relative w-32 h-56">
                {/* Bouchon amélioré */}
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-20 h-10 z-10">
                  <div className="w-full h-full bg-gradient-to-b from-gray-100 to-gray-300 rounded-t-2xl border-2 border-gray-400 shadow-lg" />
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-6 bg-gradient-to-b from-white to-gray-200 rounded-sm" />
                </div>

                {/* Flacon orangé */}
                <div className="absolute inset-0 bg-gradient-to-b from-orange-200 to-orange-300 rounded-t-3xl rounded-b-lg border-4 border-orange-800/40 shadow-2xl overflow-hidden">
                  {/* Étiquette */}
                  <div className="absolute top-16 left-0 right-0 bg-white/95 py-2 px-1 text-center border-y-2 border-orange-900/30 z-10">
                    <div className="text-xs font-bold text-orange-900">FOCUS MAX</div>
                    <div className="text-[10px] text-orange-700">PRO+++</div>
                  </div>

                  {/* Gélules empilées au fond */}
                  <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-0.5 items-end justify-center">
                    {[...Array(20)].map((_, i) => (
                      <div
                        key={i}
                        className="w-3 h-5 bg-gradient-to-b from-red-400 to-red-600 rounded-full shadow-sm"
                      />
                    ))}
                  </div>

                  {/* Reflet sur le flacon */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-transparent pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Notice */}
            <div className="rounded-lg border-2 border-orange-700 bg-white p-6 font-mono text-xs text-black">
              <div className="text-center mb-4">
                <div className="text-2xl font-bold text-orange-900">FOCUS MAX PRO+++</div>
                <div className="text-sm italic text-orange-700">"Performance sans limites"</div>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="font-bold text-red-700 uppercase mb-1">⚠️ POSOLOGIE EXTRÊME</div>
                  <div className="text-[11px]">
                    • 4 gélules toutes les 2h pendant les heures de bureau<br />
                    • Dose de choc : 8 gélules avant réunion importante<br />
                    • Maximum autorisé : <span className="font-bold">24 gélules/jour</span><br />
                    • En cas de fatigue : DOUBLER LA DOSE<br />
                    • Weekends : Maintenir la posologie (pas de repos)
                  </div>
                </div>

                <div className="bg-red-50 border-l-4 border-red-700 p-2">
                  <div className="font-bold text-red-900 uppercase mb-1">Effets secondaires</div>
                  <div className="text-[10px] text-red-800 leading-relaxed">
                    Tremblements permanents • Insomnie chronique sévère • Palpitations cardiaques •
                    Anxiété paralysante • Dépendance psychologique aigüe • Crash énergétique violent •
                    Perte totale de jugement • Hallucinations légères • Déshumanisation progressive •
                    Paranoïa corporative • Rires nerveux incontrôlés • Envie compulsive de check ses emails à 3h du matin •
                    Conviction que le week-end est une invention des faibles •
                    Développement d'un tic facial lors du mot "synergies" •
                    Incapacité à terminer une phrase sans dire "ROI" ou "KPI"
                  </div>
                </div>

                <div className="bg-yellow-50 border-2 border-yellow-600 p-2">
                  <div className="font-bold text-yellow-900 uppercase mb-1">⚠️ NE PAS UTILISER SI :</div>
                  <div className="text-[10px]">
                    • Vous dormez plus de 4h/nuit<br />
                    • Vous avez une vie en dehors du travail<br />
                    • Vous ressentez encore des émotions humaines<br />
                    • Vous pensez que "l'équilibre vie pro/perso" existe<br />
                    • Vous avez déjà prononcé le mot "pause" sans ironie<br />
                    • Vous savez encore à quoi ressemble la lumière du jour
                  </div>
                </div>

                <div className="text-[9px] text-center text-slate-600 italic mt-4">
                  Fourni gratuitement aux employés "Haute Performance Executive"<br />
                  Distribution confidentielle • Ne pas en parler aux RH<br />
                  <span className="font-bold">Si vous avez des questions, c'est que vous n'en avez pas besoin</span>
                </div>
              </div>
            </div>
          </div>
        </ModalRegister>

        {/* MODALE: Horloge Pomodoro */}
        <ModalRegister
          open={showClock}
          title="Timer Productivité Executive"
          onClose={() => {
            setShowClock(false);
            setClockRunning(false);
          }}
          containerClassName="!max-w-[600px]"
          onHint={() => setShowHintModal(true)}
        >
          <div className="py-6 flex flex-col items-center">
            {/* Horloge visuelle */}
            <div className="relative w-48 h-48 mb-6">
              {/* Cadre horloge */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 shadow-2xl border-8 border-slate-700">
                {/* Fond cadran */}
                <div className="absolute inset-4 rounded-full bg-white flex items-center justify-center">
                  {!clockRunning ? (
                    <div className="text-center">
                      <div className="text-4xl font-bold text-slate-800">20s</div>
                      <div className="text-xs text-slate-600 uppercase tracking-wider">Travail</div>
                    </div>
                  ) : (
                    <div className="text-center animate-pulse">
                      <div className="text-3xl font-bold text-emerald-600">EN COURS</div>
                      <div className="text-xs text-slate-600">REPOSEZ VOUS !</div>
                    </div>
                  )}
                </div>

                {/* Marques horaires */}
                {[0, 90, 180, 270].map((angle) => (
                  <div
                    key={angle}
                    className="absolute w-1 h-3 bg-slate-700"
                    style={{
                      top: '8px',
                      left: '50%',
                      transformOrigin: 'center 88px',
                      transform: `translateX(-50%) rotate(${angle}deg)`,
                    }}
                  />
                ))}
              </div>

              {/* Aiguille qui tourne si actif */}
              {clockRunning && (
                <div
                  className="absolute top-1/2 left-1/2 w-1 h-16 bg-red-600 rounded-full origin-bottom"
                  style={{
                    transform: 'translate(-50%, -100%)',
                    animation: 'spin 20s linear',
                  }}
                />
              )}
            </div>

            {/* Bouton */}
            <button
              onClick={() => {
                if (!clockRunning) {
                  setClockRunning(true);
                  toast?.info?.("⏰ Cycle de concentration démarré : 20 secondes.");
                }
              }}
              disabled={clockRunning}
              className={`
                px-6 py-3 rounded-lg font-semibold transition-all
                ${clockRunning
                          ? 'bg-slate-600 cursor-not-allowed opacity-50'
                          : 'bg-emerald-600 hover:bg-emerald-500 hover:scale-105'
                        }
              `}
            >
              {clockRunning ? '⏳ Pause en cours...' : '▶️ Démarrer pause'}
            </button>

            <div className="mt-4 text-xs text-slate-400 text-center italic max-w-sm">
              Timer "Pomodoro Executive" : 20 secondes de pause intense,
              puis retour immédiat pour un cycle de 10h de travail. Le repos c'est pour les faibles.
            </div>
          </div>

          {/* CSS pour l'animation */}
          <style jsx>
            {`
              @keyframes spin {
                from { transform: translate(-50%, -100%) rotate(0deg); }
                to { transform: translate(-50%, -100%) rotate(360deg); }
              }
            `}
          </style>
        </ModalRegister>

        {/* MODALE: Sculpture */}
        <ModalRegister
          open={showSculpture}
          title="Sculpture — MOMENTUM"
          onClose={() => {
            setShowSculpture(false);
            setSculptureFlipped(false);
          }}
          containerClassName="!max-w-[700px]"
          onHint={() => setShowHintModal(true)}
        >
          <div className="py-6 min-h-[600px] flex flex-col items-center justify-center">
            {/* Sculpture 3D avec flip */}
            <div
              className="relative w-80 h-[500px] cursor-pointer"
              style={{ perspective: '1000px' }}
              onClick={() => setSculptureFlipped(!sculptureFlipped)}
            >
              <div
                className={`relative w-full h-full transition-transform duration-700`}
                style={{
                  transformStyle: 'preserve-3d',
                  transform: sculptureFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                }}
              >
                {/* FACE AVANT */}
                <div
                  className="absolute inset-0 rounded-lg bg-gradient-to-br from-amber-600 to-amber-800 shadow-2xl border-4 border-amber-900/50 p-6 flex flex-col items-center justify-center"
                  style={{ backfaceVisibility: 'hidden' }}
                >
                  {/* Plaque gravée */}
                  <div className="bg-black/20 rounded p-4 text-center border border-amber-900/30 w-full max-w-sm">
                    <div className="text-2xl font-bold text-amber-100 mb-2 tracking-wider">
                      MOMENTUM
                    </div>
                    <div className="text-xs text-amber-200/80 italic mb-3">
                      par Alessandro Forte, 2023
                    </div>

                    <div className="text-[12px] text-amber-100/90 leading-relaxed mb-3">
                      Cette œuvre incarne l'esprit d'excellence<br />
                      qui anime notre entreprise :<br />
                      La force, la détermination,<br />
                      l'ambition sans limite.
                    </div>

                    <div className="text-[11px] text-amber-200/70 italic border-t border-amber-900/30 pt-2">
                      "Dans la compétition,<br />
                      il n'y a pas de seconde place."<br />
                    </div>

                    <div className="text-[10px] text-amber-200/50 mt-3">
                      Don très généreux de la direction
                    </div>
                  </div>
                </div>

                {/* FACE ARRIÈRE */}
                <div
                  className="absolute inset-0 rounded-lg bg-gradient-to-br from-amber-600 to-amber-800 shadow-2xl border-4 border-amber-900/50 p-6 flex items-center justify-center"
                  style={{
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                  }}
                >
                  <div className="bg-black/30 rounded p-8 text-center border-2 border-amber-900/40 relative overflow-hidden w-full max-w-sm">
                    {/* Bloc "Made in China" avec griffures FIXES */}
                    <div className="relative bg-amber-900/20 p-6 rounded">
                      {/* Griffures FIXES (pas de Math.random dans le render) */}
                      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.4 }}>
                        {/* Griffures horizontales */}
                        <line x1="10%" y1="35%" x2="85%" y2="37%" stroke="#1a0f0a" strokeWidth="0.5" />
                        <line x1="15%" y1="42%" x2="80%" y2="44%" stroke="#1a0f0a" strokeWidth="0.7" />
                        <line x1="20%" y1="48%" x2="90%" y2="49%" stroke="#1a0f0a" strokeWidth="0.5" />
                        <line x1="5%" y1="53%" x2="75%" y2="55%" stroke="#1a0f0a" strokeWidth="0.6" />
                        <line x1="25%" y1="60%" x2="88%" y2="61%" stroke="#1a0f0a" strokeWidth="0.5" />

                        {/* Griffures diagonales */}
                        <line x1="30%" y1="30%" x2="70%" y2="65%" stroke="#1a0f0a" strokeWidth="0.4" />
                        <line x1="15%" y1="40%" x2="50%" y2="62%" stroke="#1a0f0a" strokeWidth="0.6" />
                        <line x1="50%" y1="35%" x2="85%" y2="58%" stroke="#1a0f0a" strokeWidth="0.5" />
                        <line x1="20%" y1="50%" x2="45%" y2="68%" stroke="#1a0f0a" strokeWidth="0.5" />
                        <line x1="60%" y1="42%" x2="90%" y2="63%" stroke="#1a0f0a" strokeWidth="0.4" />

                        {/* Griffures courtes */}
                        <line x1="35%" y1="38%" x2="55%" y2="40%" stroke="#1a0f0a" strokeWidth="0.6" />
                        <line x1="45%" y1="52%" x2="68%" y2="54%" stroke="#1a0f0a" strokeWidth="0.5" />
                        <line x1="25%" y1="58%" x2="42%" y2="60%" stroke="#1a0f0a" strokeWidth="0.5" />
                        <line x1="62%" y1="47%" x2="80%" y2="49%" stroke="#1a0f0a" strokeWidth="0.6" />
                      </svg>

                      {/* Texte "Made in China" partiellement masqué */}
                      <div
                        className="text-3xl font-bold relative z-10"
                        style={{
                          fontFamily: 'Arial, sans-serif',
                          color: '#1a1a1a',
                          textShadow: '1px 1px 0px rgba(255,255,255,0.2), -1px -1px 0px rgba(0,0,0,0.4)',
                          letterSpacing: '0.05em',
                          opacity: 0.7,
                        }}
                      >
                        <div className="opacity-60 text-lg">MADE IN</div>
                        <div className="text-4xl">CHINA</div>
                      </div>
                    </div>

                    <div className="text-xs text-amber-100 mt-6 font-mono">
                      Réalisée par<br />
                      <span className="text-amber-200/70">"Art Corporate Solutions"</span>
                    </div>

                    <div className="mt-4 pt-4 border-t border-amber-900/40">
                      <div className="text-sm text-amber-100 font-bold">Prix catalogue</div>
                      <div className="text-3xl text-amber-200 font-bold">47€</div>
                      <div className="text-[10px] text-amber-200/60 mt-1">
                        (Marge appliquée : +850%)
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-xs text-slate-400 text-center italic mt-6">
              {!sculptureFlipped
                ? 'La plaque semble avoir été retournée...'
                : 'Cliquez à nouveau pour revenir'
              }
            </div>
          </div>
        </ModalRegister>

        {/* MODALE: Tiroirs */}
        <ModalRegister
          open={showDrawers}
          title="Classeur Confidentiel — Dossiers Executive"
          onClose={() => {
            setShowDrawers(false);
            setSelectedLetter(null);
          }}
          containerClassName="!max-w-[900px]"
          onHint={() => setShowHintModal(true)}
        >
          <div className="py-4">
            {!selectedLetter ? (
              /* Vue classeur avec onglets */
              <div className="space-y-4">
                <div className="text-xs text-slate-400 text-center mb-4">
                  📂 Sélectionnez une lettre pour accéder aux dossiers
                </div>

                {/* Onglets alphabétiques */}
                <div className="flex gap-1 justify-center">
                  {LETTERS.map((letter, idx) => (
                    <button
                      key={letter}
                      onClick={() => setSelectedLetter(letter)}
                      className="relative group"
                      style={{ zIndex: LETTERS.length - idx }}
                    >
                      {/* Onglet style classeur */}
                      <div className="w-16 h-24 bg-gradient-to-b from-amber-700 to-amber-900 border-2 border-amber-950 rounded-t-lg shadow-lg hover:from-amber-600 hover:to-amber-800 transition-all hover:scale-105 flex items-start justify-center pt-3">
                        <span className="text-2xl font-bold text-amber-100">
                          {letter}
                        </span>
                      </div>

                      {/* Badge nombre de dossiers */}
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 rounded-full border-2 border-white text-white text-xs font-bold flex items-center justify-center">
                        {EMPLOYEE_FILES[letter]?.length || 0}
                      </div>
                    </button>
                  ))}
                </div>

                <div className="mt-6 rounded-lg border border-yellow-600/30 bg-yellow-600/10 p-3 text-center">
                  <div className="text-xs text-yellow-200">
                    ⚠️ Classification : CONFIDENTIEL EXECUTIVE<br />
                    Accès non autorisé passible de sanctions
                  </div>
                </div>
              </div>
            ) : (
              /* Vue dossiers de la lettre sélectionnée */
              <div className="space-y-4">
                <button
                  onClick={() => setSelectedLetter(null)}
                  className="text-xs text-cyan-400 hover:underline flex items-center gap-2 mb-4"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Retour au classeur
                </button>

                <div className="text-sm text-slate-300 mb-4">
                  📁 Dossiers — Lettre <span className="font-bold text-amber-400">{selectedLetter}</span>
                </div>

                {/* Liste des dossiers */}
                <div className="space-y-2">
                  {EMPLOYEE_FILES[selectedLetter]?.map((file) => (
                    <div
                      key={file.id}
                      className={`
                        rounded-lg border p-4 transition-all
                        ${file.locked
                                  ? 'border-red-500/30 bg-red-500/5 cursor-not-allowed opacity-60'
                                  : 'border-slate-700 bg-slate-800/40 hover:bg-slate-800/60 hover:border-amber-500/50'
                                }
                      `}
                      onClick={() => {
                        if (file.locked) {
                          toast?.error?.("🔒 Accès refusé : Autorisation Executive requise");
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">{file.flag}</div>
                        <div className="flex-1">
                          <div className="font-semibold text-white mb-1"> {/* ✅ Blanc */}
                            {file.name}
                          </div>
                          <div className="text-xs text-white leading-relaxed"> {/* ✅ Blanc */}
                            {file.note}
                          </div>
                        </div>
                        {file.locked && (
                          <div className="text-xl">🔒</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ModalRegister>

        <HintsModalHub
          open={showHintModal}
          onClose={() => setShowHintModal(false)}
          title="Indices — Joueur 6 (Bureau Exécutif)"
          markHintUsedOnce={markHintUsedOnce}
          categories={hintCategories}
          usedStorageKey={`hints_used:${code}:s2:p${playerId}`}
        />
      </div>
    </div>
  );
}